if (!customElements.get('cta-cross-sell')) {
  customElements.define('cta-cross-sell', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      if (!this.form || this.initialized) return;
      this.initialized = true;
      this.check = this.querySelector('[data-cross-check]');
      this.radios = [...this.querySelectorAll('[data-cross-variant]')];
      this.colors = this.querySelector('[data-cross-colors]');
      this.details = this.querySelector('[data-cross-details]');
      this.toggle = this.querySelector('[data-cross-details-toggle]');
      this.button = this.form.querySelector('[name="add"]');
      this.buttonText = this.button.querySelector('span');
      this.normalLabel = this.buttonText.textContent.trim();
      this.prices = JSON.parse(this.querySelector('[data-cross-main-prices]').textContent);
      this.quantity = this.form.elements.namedItem('quantity');
      this.image = this.querySelector('.cta-cross__image');
      // Use the configured/product image only when the default variant has no image.
      if (this.image && !this.defaultImage) {
        this.defaultImage = {
          src: this.image.getAttribute('src'),
          srcset: this.image.getAttribute('srcset'),
          alt: this.image.alt,
        };
      }
      this.onChange = (event) => {
        if (event.target === this.check) {
          if (this.check.checked) {
            const first = this.radios.find((radio) => !radio.disabled);
            if (!first) { this.check.checked = false; return; }
            first.checked = true;
            this.colors.classList.remove('cta-cross__highlight');
            void this.colors.offsetWidth;
            this.colors.classList.add('cta-cross__highlight');
          } else {
            this.detailsOpen = false;
            if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
          }
          this.setExpanded(this.colors, this.check.checked);
          this.check.setAttribute('aria-expanded', String(this.check.checked));
        }
        this.update();
      };
      this.addEventListener('change', this.onChange);
      this.onQuantity = () => this.update();
      this.quantity?.addEventListener('change', this.onQuantity);
      this.quantity?.addEventListener('input', this.onQuantity);
      this.onToggle = () => { this.detailsOpen = !this.detailsOpen; this.update(); };
      this.toggle?.addEventListener('click', this.onToggle);
      this.unsubscribers = [
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event.data.sectionId) !== this.dataset.section) return;
          // The native variant handler restores its own button label first.
          if (!this.button.disabled) this.normalLabel = this.buttonText.textContent.trim();
          this.update();
        }),
        subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantity),
      ];
      this.check.disabled = !this.radios.some((radio) => !radio.disabled);
      this.querySelector('[data-cross-unavailable]').hidden = !this.check.disabled;
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.querySelector('.cta-cross__card'));
      this.hidden = false;
      this.update();
    }

    disconnectedCallback() {
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      this.resizeObserver?.disconnect();
      this.removeEventListener('change', this.onChange);
      this.quantity?.removeEventListener('change', this.onQuantity);
      this.quantity?.removeEventListener('input', this.onQuantity);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.initialized = false;
    }

    get selected() { return this.radios.find((radio) => radio.checked && !radio.disabled); }
    get variantId() { return this.check.checked ? this.selected?.value : null; }

    setExpanded(element, expanded) {
      if (!element) return;
      element.inert = !expanded;
      element.style.maxHeight = expanded ? `${element.scrollHeight}px` : '0px';
    }

    resize() {
      if (this.detailsOpen && this.details) this.details.style.maxHeight = `${this.details.scrollHeight}px`;
      if (this.check.checked) {
        // Include the final details height while its transition is still running.
        const height = [...this.colors.children].reduce((sum, child) => sum + (child === this.details ? (this.detailsOpen ? child.scrollHeight : 0) : child.getBoundingClientRect().height), 0);
        this.colors.style.maxHeight = `${Math.ceil(height + 4)}px`;
      }
    }

    update() {
      const selected = this.selected;
      const enabled = this.check.checked && !!selected;
      const preview = selected || this.radios.find((radio) => !radio.disabled) || this.radios[0];
      const money = (value) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value / 100);
      const qty = Math.max(1, Number.parseInt(this.quantity?.value || '1', 10) || 1);
      const mainPrice = this.prices[this.form.elements.namedItem('id').value];
      const count = qty + (enabled ? 1 : 0);
      if (this.image && this.defaultImage) {
        const imageVariant = enabled ? selected : this.radios.find((radio) => !radio.disabled);
        const variantImage = imageVariant?.dataset.image;
        const src = variantImage || this.defaultImage.src;
        if (this.image.getAttribute('src') !== src) this.image.setAttribute('src', src);
        if (variantImage) {
          this.image.removeAttribute('srcset');
          this.image.alt = `${this.defaultImage.alt} ${imageVariant.dataset.title}`;
        } else {
          if (this.defaultImage.srcset) this.image.setAttribute('srcset', this.defaultImage.srcset);
          else this.image.removeAttribute('srcset');
          this.image.alt = this.defaultImage.alt;
        }
      }
      const label = this.querySelector('[data-cross-selected]');
      label.hidden = !enabled;
      label.replaceChildren();
      if (enabled) {
        const name = document.createElement('strong');
        name.textContent = selected.dataset.title;
        label.append(document.createTextNode('選択中：'), name);
      }
      this.classList.toggle('is-on', enabled);
      this.classList.toggle('is-open', enabled && !!this.detailsOpen);
      this.querySelector('[data-cross-color-name]').textContent = enabled ? selected.dataset.title : '';
      this.querySelector('[data-cross-price]').textContent = preview ? `＋${money(Number(preview.dataset.price))}` : '';
      this.querySelector('[data-cross-count]').textContent = `商品${count}点`;
      this.querySelector('[data-cross-total]').textContent = mainPrice == null ? '—' : money(mainPrice * qty + (enabled ? Number(selected.dataset.price) : 0));
      if (!this.button.disabled && this.button.getAttribute('aria-disabled') !== 'true') {
        this.buttonText.textContent = enabled ? `${count}点をカートに追加する` : this.normalLabel;
      }
      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', String(!!this.detailsOpen));
        this.toggle.textContent = this.detailsOpen ? '閉じる ▴' : `${this.toggle.dataset.label} ▾`;
        this.setExpanded(this.details, !!this.detailsOpen);
      }
      this.resize();
    }
  });
}
